import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { adminActions } from '@sonar-software/sonar-admin';
import { AsyncStateNotification } from '@ui/notifications';
import { createAsyncSelectors } from '@lib/selectors';
import { MoreOutlined } from '@ant-design/icons';
import { SonarModal } from '@ui/modals';
import { useAction } from '@hooks';
import { Button, Dropdown, Form, Input, MenuProps } from 'antd';
import { coreActions, Customer } from '@sonar-software/sonar-core';
import React, { useCallback, useEffect, useState } from 'react';

const reindexCustomerSelectors = createAsyncSelectors((state) => state.admin.search.shardCatalog.reindexCustomer.post);

interface RenameCustomerFormValues {
	name: string;
}

export interface CustomerViewExtraDropdownProps {
	customer?: Customer;
	shardName?: string;
}

export const CustomerViewExtraDropdown: React.FC<CustomerViewExtraDropdownProps> = (props) => {
	const { customer, shardName } = props;

	const [ form ] = Form.useForm<RenameCustomerFormValues>();

	const [ isRenameCustomerModalVisible, setIsRenameCustomerModalVisible ] = useState(false);

	const lockAllCustomers = useAction(adminActions.requestPostAdminCustomersLockAllUsers);

	const handleLockAllUsersClicked = useCallback(() => {
		if (customer) {
			lockAllCustomers({
				customerId: customer.id,
				shardName
			});
		}
	}, [ lockAllCustomers, customer, shardName ]);

	const patchCustomer = useAction(coreActions.requestPatchCoreCustomersById);

	const handleLockCustomerClicked = useCallback(() => {
		if (customer) {
			patchCustomer({
				customerId: customer.id,
				isLocked: !customer.isLocked,
				shardName
			});
		}
	}, [ patchCustomer, customer, shardName ]);

	const handleRenameCustomerClicked = useCallback(() => {
		setIsRenameCustomerModalVisible(true);
	}, [ setIsRenameCustomerModalVisible ]);

	const handleCustomerRenamed = useCallback(() => {
		form.validateFields().then((values) => {
			patchCustomer({
				customerId: customer?.id,
				name: values.name,
				shardName
			});
			setIsRenameCustomerModalVisible(false);
		});
	}, [ form, setIsRenameCustomerModalVisible, customer, shardName, patchCustomer ]);

	const handleCustomerRenameCanceled = useCallback(() => {
		setIsRenameCustomerModalVisible(false);
	}, [ setIsRenameCustomerModalVisible ]);

	const reindexCustomer = useAction(adminActions.requestPostAdminSearchShardCatalogReindexCustomer);

	const handleReindexInShardCatalogClicked = useCallback(() => {
		if (customer) {
			reindexCustomer({
				customerId: customer.id,
				shardName
			});
		}
	}, [ reindexCustomer, customer, shardName ]);

	useEffect(() => {
		if (isRenameCustomerModalVisible) {
			form.resetFields();
		}
	}, [ form, isRenameCustomerModalVisible ]);

	const menuProps: MenuProps = {
		items: [
			{
				key: '1',
				label: ADMIN_DASHBOARD_COPY.LOCK_ALL_USERS,
				onClick: handleLockAllUsersClicked
			},
			{
				key: '2',
				label: customer?.isLocked ? ADMIN_DASHBOARD_COPY.UNLOCK_CUSTOMER : ADMIN_DASHBOARD_COPY.LOCK_CUSTOMER,
				onClick: handleLockCustomerClicked
			},
			{
				key: '3',
				label: ADMIN_DASHBOARD_COPY.RENAME_CUSTOMER,
				onClick: handleRenameCustomerClicked
			},
			{
				key: '4',
				label: ADMIN_DASHBOARD_COPY.REINDEX_IN_SHARD_CATALOG,
				onClick: handleReindexInShardCatalogClicked
			}
		]
	};

	return (
		<span>
			<Dropdown
				menu={ menuProps }
				trigger={ [ 'click' ] }>
				<Button className='customer-view-extra-dropdown-btn'>
					<MoreOutlined />
				</Button>
			</Dropdown>
			<SonarModal
				onCancel={ handleCustomerRenameCanceled }
				onOk={ handleCustomerRenamed }
				open={ isRenameCustomerModalVisible }
				title={ ADMIN_DASHBOARD_COPY.RENAME_CUSTOMER }>
				<Form
					className='rename-customer-form inline-form-with-overhead-labels'
					colon={ false }
					form={ form }
					initialValues={ {
						name: customer?.name
					} }
					layout='inline'>
					<Form.Item
						label={ ADMIN_DASHBOARD_COPY.NEW_CUSTOMER_NAME }
						name='name'
						rules={ [
							{
								message: ADMIN_DASHBOARD_COPY.CUSTOMER_NAME_REQUIRED,
								required: true
							}
						] }>
						<Input />
					</Form.Item>
				</Form>
			</SonarModal>
			<AsyncStateNotification
				errored={ ADMIN_DASHBOARD_COPY.REINDEX_CUSTOMER_FAILED }
				failed={ ADMIN_DASHBOARD_COPY.REINDEX_CUSTOMER_FAILED }
				requested={ ADMIN_DASHBOARD_COPY.REINDEX_CUSTOMER_REQUESTED }
				stateSelector={ reindexCustomerSelectors.state } />
		</span>
	);
};
